import Vue from 'vue';
import VueCompositionAPI from '@vue/composition-api';
import { provideConfigurationApi, VueConfigurationPlugin } from './plugins/configuration';
import { VueBreakpointPlugin } from './plugins/breakpoint';
import { VueAssetPlugin } from './plugins/assets';
import { provideTracking, VueTrackingPlugin } from './plugins/tracking';
import VueI18Next from '@panter/vue-i18next';
import Toast from 'vue-toastification';
import PortalVue from 'portal-vue';
import FancyColor from './directive/style/FancyColor';
import ClickOutside from './directive/events/ClickOutside';
import Tooltip from './directive/ui/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import VueElementLoading from 'vue-element-loading';
import { provideToast } from 'vue-toastification/composition';
import { provideI18n } from './composables/use-vue-i18n';
import i18next from 'i18next';
import { TouchSwipe } from '@a/directive/ui/touchswipe/TouchSwipe';
import GPortalStripe from '@gportal/stripe';

export const useVueFeatures = () => {
  const toastOptions = {
    timeout: 10000,
    icon: false,
    toastClassName: 'gp-toast',
    closeButtonClassName: 'gp-toast_close',
  };

  /**
   * used for our vue plugins
   */
  const installPlugins = (): void => {
    Vue
      .use(VueCompositionAPI)
      .use(VueConfigurationPlugin)
      .use(VueBreakpointPlugin)
      .use(VueAssetPlugin)
      .use(VueTrackingPlugin)
      .use(VueI18Next)
      .use(Toast, toastOptions)
      .use(GPortalStripe, {
        publicKey: Vue.prototype.$api.data.stripe.publicKey,
      })
      .use(PortalVue);
  };

  /**
   * used for installing our vue directives
   */
  const installDirectives = (): void => {
    Vue.directive('fancy-color', FancyColor);
    Vue.directive('click-outside', ClickOutside);
    Vue.directive('tooltip', Tooltip);
    Vue.directive('touch-swipe', TouchSwipe());
  };

  /**
   * used for installing our global vue components
   */
  const installGlobalComponents = (): void => {
    Vue.component('font-awesome-icon', FontAwesomeIcon);
    Vue.component('VueElementLoading', VueElementLoading);
  };

  /**
   * used for dependency injection of our services into the @vue/composition-api
   * components
   */
  const provideContainer = (): void => {
    provideToast(toastOptions);
    provideI18n();
    provideConfigurationApi();
    provideTracking();
  };

  /**
   * returns true if the development is turned on
   */
  const isDevelopmentModeActive = (): boolean => process.env.NODE_ENV === 'production' || process.env.VUE_APP_DEBUG === 'true';

  /**
   * promise which is resolved when i18next is loaded
   */
  const isI18NextReady = (
    options: { graceTimeout: number } = { graceTimeout: 1000 },
  ): Promise<string> => new Promise<string>((resolve, reject) => {
    const handle = window.setTimeout(() => {
      resolve(`i18next initialized after graceTimeout (value = ${options.graceTimeout})`);
    }, options.graceTimeout);

    i18next.on('initialized',
      (loaded: boolean) => {
        window.clearTimeout(handle);

        if (loaded) {
          resolve('i18next initialized with i18next.on initialized (loaded = true)');
        } else { reject(new Error('i18next initialized failed')); }
      });

    if (i18next.isInitialized) {
      window.clearTimeout(handle);

      resolve('i18next initialized with i18next.isInitialized');
    }
  });

  return {
    isDevelopmentModeActive,
    installPlugins,
    installDirectives,
    installGlobalComponents,
    provideContainer,
    isI18NextReady,
  };
};
